/**
 * Manager Payment Detail — admin/finance wanaona maelezo kamili ya invoice,
 * jina la member, na PDF download buttons.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { paymentsAPI, invoiceAPI } from '../../services/api';

export default function ManagerPaymentDetail() {
  const { id } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState('');
  const [pdfError, setPdfError] = useState('');

  async function handlePdf(type) {
    setPdfLoading(type);
    setPdfError('');
    try {
      if (type === 'invoice') await invoiceAPI.openPdf(Number(id));
      else await invoiceAPI.openReceipt(Number(id));
    } catch (e) {
      setPdfError(e.message || 'Failed to download PDF.');
    } finally {
      setPdfLoading('');
    }
  }

  useEffect(() => {
    paymentsAPI.get(id)
      .then((resp) => setPayment(resp))
      .catch((e) => setError(e.response?.data?.error || 'Failed to load payment details.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="text-gray-600">Loading...</p>;
  if (error) return <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>;
  if (!payment) return <div className="rounded-2xl border border-slate-200 bg-white p-4 text-slate-600">Payment not found.</div>;

  const paid = String(payment.paystatus || '').toUpperCase() === 'PAID';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/payments" className="text-sm text-blue-600 hover:underline">← Payments</Link>
        <h1 className="text-2xl font-bold text-gray-800">Payment Detail</h1>
      </div>

      {/* Reference + status */}
      <div className="rounded-[28px] border-2 border-blue-200 bg-blue-50 p-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">CRDB Payment Reference</p>
        <p className="mt-2 font-mono text-4xl font-bold tracking-wider text-blue-900">{payment.reference_no}</p>
        <div className="mt-3 flex items-center justify-center gap-3">
          <span className={`rounded-full px-4 py-1.5 text-sm font-semibold ${paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {paid ? '✓ PAID' : 'PENDING'}
          </span>
          <span className="text-sm text-blue-700">
            TZS {Number(payment.fee || 0).toLocaleString()} — Year {payment.fee_year}
          </span>
        </div>
      </div>

      {/* PDF buttons */}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => handlePdf('invoice')} disabled={pdfLoading !== ''}
          className="flex items-center gap-2 rounded-2xl border border-blue-200 bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 shadow-sm hover:bg-blue-50 disabled:opacity-60">
          {pdfLoading === 'invoice' ? '⏳ Downloading...' : '📄 Download Invoice PDF'}
        </button>
        {paid && (
          <button type="button" onClick={() => handlePdf('receipt')} disabled={pdfLoading !== ''}
            className="flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60">
            {pdfLoading === 'receipt' ? '⏳ Downloading...' : '🧾 Download Receipt PDF'}
          </button>
        )}
      </div>
      {pdfError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{pdfError}</div>
      )}

      {/* Info cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        <InfoCard title="Invoice Info" rows={[
          ['Reference No', payment.reference_no || '-'],
          ['Fee Year', payment.fee_year || '-'],
          ['Fee Type', 'AWTF (Welfare Fund)'],
          ['Due Date', payment.duedate || '-'],
        ]} />
        <InfoCard title="Amount & Status" rows={[
          ['Amount', `TZS ${Number(payment.fee || 0).toLocaleString()}`],
          ['Status', String(payment.paystatus || 'PENDING').toUpperCase()],
          ['Member ID', payment.member_id || '-'],
          ['Created', payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'],
        ]} />
      </div>
    </div>
  );
}

function InfoCard({ title, rows }) {
  return (
    <div className="rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      <div className="mt-4 space-y-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
            <span className="text-sm text-slate-500">{label}</span>
            <span className="text-sm font-semibold text-slate-900">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
